import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { api } from "../api";
import { ReferredByCard } from "../components/ReferredByCard";
import { RefcodeWalletLink } from "../components/RefcodeWalletLink";
import { PointsChart } from "../components/PointsChart";
import { WalletPointsShare } from "../components/WalletPointsShare";
import { ReferralContributionsTable } from "../components/ReferralContributionsTable";
import { WalletAirdrops } from "../components/WalletAirdrops";
import { WalletPrizeHistory } from "../components/WalletPrizeHistory";
import { WalletPnl } from "../components/WalletPnl";
import { Collapsible, openSection } from "../components/Collapsible";
import { Skeleton } from "../components/Skeleton";
import { formatSignedUsd } from "../utils/format";
import { explorerLink, shortAddress } from "../utils/address";
import { priceSpins, summarizeSpins } from "../utils/pnl";

const SECTIONS = [
  { id: "wallet-pnl", label: "Profit & loss" },
  { id: "wallet-points", label: "Points" },
  { id: "wallet-points-history", label: "Points over time" },
  { id: "wallet-points-share", label: "Share" },
  { id: "wallet-airdrops", label: "Airdrops" },
  { id: "wallet-prizes", label: "Prizes" },
];

const netClass = (n: number) =>
  n > 0 ? "text-green-400" : n < 0 ? "text-red-400" : "text-gray-50";

const timeAgo = (t: number): string => {
  const mins = Math.max(0, Math.round((Date.now() - t) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
};

export function WalletDetail() {
  const { address } = useParams<{ address: string }>();
  const [copied, setCopied] = useState(false);

  // Fetch referrer data
  const referrerQuery = useQuery({
    queryKey: ["wallet-referrer", address],
    queryFn: async () => {
      if (!address) return null;
      const { data, error } = await api.wallet({ walletAddress: address })
        .referrer.get();
      if (error) {
        // 404 is not an error - just means no referrer data
        if (error.value && typeof error.value === "object" && "error" in error.value) {
          return null;
        }
        throw error;
      }
      return data;
    },
    enabled: !!address,
  });

  // Fetch points data
  const pointsQuery = useQuery({
    queryKey: ["wallet-points", address],
    queryFn: async () => {
      if (!address) return null;
      const { data, error } = await api.wallet({ walletAddress: address })
        .points.get();
      if (error) {
        // 404 is not an error - just means no points data
        if (error.value && typeof error.value === "object" && "error" in error.value) {
          return null;
        }
        throw error;
      }
      return data;
    },
    enabled: !!address,
  });

  // Same query the PnL section uses (deduplicated by React Query), for the header summary
  const pnlQuery = useQuery({
    queryKey: ["wallet-pnl", address, null],
    queryFn: async () => {
      const { data, error } = await api.wallet({ walletAddress: address! }).pnl.get({ query: {} });
      if (error) throw error;
      return data;
    },
    enabled: !!address,
    staleTime: 60_000,
  });
  const headline = useMemo(() => {
    if (!pnlQuery.data) return null;
    const { spins } = priceSpins(pnlQuery.data.data.events, pnlQuery.data.data.machines);
    const summary = summarizeSpins(spins);
    return summary
      ? { net: summary.net, spins: spins.length, last: summary.last, freePacks: summary.freeCount, actual: summary.actualCount > 0 }
      : null;
  }, [pnlQuery.data]);

  const handleCopy = async () => {
    if (address) {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!address) {
    return (
      <div className="card">
        <p className="text-red-400">That's not a valid wallet address.</p>
      </div>
    );
  }

  const points = pointsQuery.data?.data;
  const referrer = referrerQuery.data?.data;
  const referredPacks = referrer?.referredPacks ?? {};
  // Most active referrals first; wallets with no pack data keep API order.
  const referredWallets = [...(referrer?.referredWallets ?? [])].sort(
    (a, b) => (referredPacks[b] ?? 0) - (referredPacks[a] ?? 0)
  );
  const totalReferredPacks = referrer?.totalReferredPacks ?? null;

  return (
    <div className="space-y-6">
      {/* Wallet header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="page-title">Wallet</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <code
              className="font-mono text-sm text-blue-400 bg-gray-800 border border-gray-700 px-3 py-1.5 rounded-lg"
              title={address}
            >
              <span className="sm:hidden">{shortAddress(address, 6)}</span>
              <span className="hidden sm:inline break-all">{address}</span>
            </code>
            <button onClick={handleCopy} className="btn btn-secondary text-sm px-3 py-1.5" aria-label="Copy wallet address">
              {copied ? "Copied" : "Copy"}
            </button>
            <a
              href={explorerLink(address).href}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost text-sm px-3 py-1.5"
            >
              {explorerLink(address).label}
            </a>
          </div>
        </div>
        <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-gray-500">Net result</dt>
            <dd className={`text-xl font-semibold num ${netClass(headline?.net ?? 0)}`}>
              {pnlQuery.isLoading ? "…" : headline ? formatSignedUsd(headline.net) : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Spins</dt>
            <dd className="text-xl font-semibold num text-gray-50">
              {pnlQuery.isLoading ? "…" : headline ? headline.spins.toLocaleString() : "0"}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Last spin</dt>
            <dd className="text-xl font-semibold num text-gray-50">
              {pnlQuery.isLoading ? "…" : headline ? timeAgo(headline.last) : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Points</dt>
            <dd className="text-xl font-semibold num text-gray-50">
              {points ? points.totalPoints?.toLocaleString() : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Refcode</dt>
            <dd className="text-xl font-semibold font-mono text-yellow-400">
              {referrer?.refcode || "—"}
            </dd>
          </div>
        </dl>
      </div>

      {/* Section jump links */}
      <nav className="subnav" aria-label="Sections">
        {SECTIONS.map((s) => (
          <button key={s.id} className="subnav-link" onClick={() => openSection(s.id)}>
            {s.label}
          </button>
        ))}
      </nav>

      {/* Profit & loss */}
      <section className="card" id="wallet-pnl" data-section>
        <div className="mb-4">
          <h2 className="section-title">Profit &amp; loss</h2>
          <p className="section-hint">
            Each spin costs the machine price and every card is assumed sold straight back through
            the machine's instant buyback (buyback rate × insured value). Import the wallet's spin
            history below to use what was actually paid and received; cards the wallet kept then
            count at their insured value.
          </p>
        </div>
        <WalletPnl walletAddress={address} />
      </section>

      {/* Points + referral */}
      <div className="grid md:grid-cols-2 gap-6" id="wallet-points" data-section>
        <section className="card">
          <h2 className="section-title mb-4">Points</h2>
          {pointsQuery.isLoading && <Skeleton kind="lines" rows={6} />}
          {pointsQuery.error && <p className="text-red-400">Couldn't load points.</p>}
          {points && (
            <dl className="space-y-2 text-sm">
              <Row label="Total" value={points.totalPoints} strong />
              <Row label="Primary" value={points.primaryPoints} />
              <Row label="Secondary" value={points.secondaryPoints} />
              <Row label="Bonus" value={points.bonusPoints} />
              <div className="border-t border-gray-700 pt-2">
                <Row label="Used" value={points.pointsUsed} />
                <Row label="Remaining" value={points.pointsRemaining} strong />
              </div>
            </dl>
          )}
          {!pointsQuery.isLoading && !points && (
            <p className="text-gray-500">
              No points recorded for this wallet yet. Points are fetched after the wallet's first
              win.
            </p>
          )}
        </section>

        <section className="card">
          <h2 className="section-title mb-4">Referrals</h2>
          {referrerQuery.isLoading && <Skeleton kind="lines" rows={4} />}
          {referrerQuery.error && <p className="text-red-400">Couldn't load referral data.</p>}
          {referrer && (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-gray-500">Referral code</div>
                <div className="font-mono text-lg text-yellow-400">{referrer.refcode || "None"}</div>
              </div>
              {referrer.referredBy && <ReferredByCard refcode={referrer.referredBy} />}
              <div className="flex gap-8">
                <div>
                  <div className="text-gray-500">Wallets referred</div>
                  <div className="text-2xl font-semibold num text-gray-50">{referredWallets.length}</div>
                </div>
                {totalReferredPacks !== null && (
                  <div>
                    <div className="text-gray-500">Packs by referrals</div>
                    <div className="text-2xl font-semibold num text-gray-50">
                      {totalReferredPacks.toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {!referrerQuery.isLoading && !referrer && (
            <p className="text-gray-500">No referral data for this wallet.</p>
          )}
        </section>
      </div>

      {/* Points history */}
      <Collapsible id="wallet-points-history" title="Points over time" defaultOpen>
        <PointsChart
          walletAddress={address}
          pointsUsed={points?.pointsUsed ?? null}
          freePacksRedeemed={headline?.actual ? headline.freePacks : null}
        />
      </Collapsible>

      {/* Points share */}
      <Collapsible
        id="wallet-points-share"
        title="Share of platform points"
        hint="This wallet's share of all points gained during a quarter."
      >
        <WalletPointsShare walletAddress={address} />
      </Collapsible>

      {/* $CARDS airdrops */}
      <Collapsible
        id="wallet-airdrops"
        title="$CARDS airdrops"
        hint="$CARDS received in each airdrop, next to wallets that gained a similar amount of points that quarter."
      >
        <WalletAirdrops walletAddress={address} />
      </Collapsible>

      {/* Referral contributions */}
      {referredWallets.length > 0 && (
        <Collapsible
          id="wallet-referral-contributions"
          title="Referral contributions"
          hint="How much each referred wallet has gained recently."
          summary={`${referredWallets.length} referred`}
        >
          <ReferralContributionsTable walletAddress={address} />
        </Collapsible>
      )}

      {/* Referred wallets */}
      {referredWallets.length > 0 && (
        <Collapsible
          id="wallet-referred"
          title="Referred wallets"
          summary={
            totalReferredPacks !== null
              ? `${referredWallets.length.toLocaleString()} · ${totalReferredPacks.toLocaleString()} packs`
              : referredWallets.length.toLocaleString()
          }
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {referredWallets.map((refcode) => (
              <div key={refcode} className="card-inset px-3 py-2 flex items-center justify-between gap-3">
                <RefcodeWalletLink refcode={refcode} />
                {referredPacks[refcode] !== undefined && (
                  <span className="num text-xs text-gray-400 whitespace-nowrap">
                    {referredPacks[refcode].toLocaleString()} {referredPacks[refcode] === 1 ? "pack" : "packs"}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Collapsible>
      )}

      {/* Prize history */}
      <Collapsible id="wallet-prizes" title="Prize history" defaultOpen>
        <WalletPrizeHistory walletAddress={address} />
      </Collapsible>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: number | null | undefined; strong?: boolean }) {
  return (
    <div className="flex justify-between items-center py-0.5">
      <dt className="text-gray-400">{label}</dt>
      <dd className={`num ${strong ? "text-gray-50 font-semibold text-base" : "text-gray-300"}`}>
        {value?.toLocaleString() ?? "—"}
      </dd>
    </div>
  );
}
